/**
 * Global Data Store - Single Source of Truth for Mock Data
 * 
 * This module ensures data consistency across the application by:
 * 1. Generating all mock data once and caching it
 * 2. Providing a single source of truth for all pages
 * 3. Ensuring Homepage metrics match detail screen data
 */

import { addDays, subDays, isToday, isWithinInterval } from 'date-fns';
import type { PromiseToPay, CollectionCase } from '@/types/collections';
import type { Receipt } from '@/types/payments';

// Import existing generators
import { generateMockPromises, generateMockCases } from './collections-mock-data';
import { generateMockReceipts } from '@/data/payments-mock-data';

/**
 * Global Data Store Singleton
 * Ensures all components use the same data
 */
class GlobalDataStore {
  // Reset instance to null to force regeneration with updated mock data
  private static instance: GlobalDataStore | null = null;
  
  // Cached data
  private promises: PromiseToPay[] = [];
  private cases: CollectionCase[] = [];
  private receipts: Receipt[] = [];
  private disputes: any[] = [];
  
  private constructor() {
    this.initializeData();
  }
  
  /**
   * Get singleton instance
   */
  public static getInstance(): GlobalDataStore {
    if (!GlobalDataStore.instance) {
      GlobalDataStore.instance = new GlobalDataStore();
    }
    return GlobalDataStore.instance;
  }
  
  /**
   * Reset the store (useful for testing or data refresh)
   */
  public static reset(): void {
    GlobalDataStore.instance = null;
  }
  
  /**
   * Initialize all mock data
   */
  private initializeData(): void {
    // Generate promises
    this.promises = generateMockPromises();
    
    // Generate cases
    this.cases = generateMockCases();
    
    // Generate receipts
    this.receipts = generateMockReceipts();
    
    // Generate disputes
    this.disputes = this.generateMockDisputes();
  }
  
  /**
   * Generate mock disputes
   */
  private generateMockDisputes(): any[] {
    return [
      {
        id: "DSP-001",
        customer: "Acme Corporation",
        customerId: "1",
        type: "Billing Error",
        amount: 5420.00,
        status: "In Progress",
        priority: "High",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 85,
        aiRecommendation: "Approve partial credit of 30% based on similar cases",
        currentLevel: 2,
        totalLevels: 4,
        description: "Customer disputes billing amount for services rendered in December. Claims that agreed-upon discount was not applied to the invoice.",
        resolutionNotes: "",
      },
      {
        id: "DSP-002",
        customer: "TechStart Inc",
        customerId: "2",
        type: "Service Quality",
        amount: 12800.00,
        status: "Under Review",
        priority: "Critical",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 2),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Escalated Quality Dispute",
        aiScore: 72,
        aiRecommendation: "Escalate to senior management for review",
        currentLevel: 3,
        totalLevels: 4,
        description: "Major quality issues reported with delivered products. Customer requesting full refund and compensation.",
        resolutionNotes: "",
      },
      {
        id: "DSP-003",
        customer: "Global Solutions Ltd",
        customerId: "3",
        type: "Payment Dispute",
        amount: 8950.00,
        status: "New",
        priority: "Medium",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 91,
        aiRecommendation: "Verify payment records and contact customer",
        currentLevel: 1,
        totalLevels: 4,
        description: "Customer claims payment was already made but invoice shows outstanding balance.",
        resolutionNotes: "",
      },
      {
        id: "DSP-004",
        customer: "Enterprise Systems Co",
        customerId: "4",
        type: "Duplicate Charge",
        amount: 3200.00,
        status: "Resolved",
        priority: "Low",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 10),
        dueDate: subDays(new Date(), 3),
        requiresResponse: false,
        workflow: "Quick Resolution",
        aiScore: 95,
        aiRecommendation: "Issue credit immediately",
        currentLevel: 4,
        totalLevels: 4,
        description: "Duplicate invoice sent to customer. Credit issued and confirmed.",
        resolutionNotes: "Credit memo issued. Customer confirmed receipt and satisfaction.",
      },
      {
        id: "DSP-005",
        customer: "Innovate Labs",
        customerId: "5",
        type: "Contract Terms",
        amount: 18500.00,
        status: "Escalated",
        priority: "High",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 5),
        dueDate: addDays(new Date(), 2),
        requiresResponse: false,
        workflow: "Legal Review Required",
        aiScore: 68,
        aiRecommendation: "Consult legal team for contract interpretation",
        currentLevel: 3,
        totalLevels: 4,
        description: "Dispute over contract terms and pricing structure. Requires legal review.",
        resolutionNotes: "",
      },
      {
        id: "DSP-006",
        customer: "Digital Dynamics",
        customerId: "6",
        type: "Billing Error",
        amount: 6750.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 88,
        aiRecommendation: "Review invoice and issue correction",
        currentLevel: 2,
        totalLevels: 4,
        description: "Customer disputes invoice total. Calculation error suspected.",
        resolutionNotes: "",
      },
    ];
  }
  
  // ============================================
  // PROMISES QUERIES
  // ============================================
  
  /**
   * Get all promises
   */
  public getAllPromises(): PromiseToPay[] {
    return this.promises;
  }
  
  /**
   * Get promises due today
   */
  public getPromisesDueToday(): PromiseToPay[] {
    const today = new Date();
    return this.promises.filter(p => {
      const promiseDate = new Date(p.promiseDate);
      return isToday(promiseDate) && p.status === 'Active';
    });
  }
  
  /**
   * Get promises due this week
   */
  public getPromisesDueThisWeek(): PromiseToPay[] {
    const today = new Date();
    const endOfWeek = addDays(today, 7);
    return this.promises.filter(p => {
      const promiseDate = new Date(p.promiseDate);
      return isWithinInterval(promiseDate, { start: today, end: endOfWeek }) && p.status === 'Active';
    });
  }
  
  /**
   * Get broken promises
   */
  public getBrokenPromises(): PromiseToPay[] {
    return this.promises.filter(p => p.status === 'Broken');
  }
  
  /**
   * Get promises kept today
   */
  public getPromisesKeptToday(): PromiseToPay[] {
    return this.promises.filter(p => {
      // In a real system, this would check when the promise was fulfilled
      // For mock data, we'll return a subset of 'Paid' promises
      return p.status === 'Paid';
    }).slice(0, 5); // Return first 5 for demo
  }
  
  // ============================================
  // CASES QUERIES
  // ============================================
  
  /**
   * Get all cases
   */
  public getAllCases(): CollectionCase[] {
    return this.cases;
  }
  
  /**
   * Get cases due today
   */
  public getCasesDueToday(): CollectionCase[] {
    const today = new Date();
    return this.cases.filter(c => {
      const actionDate = new Date(c.nextActionDate);
      return isToday(actionDate) && c.status === 'Active';
    });
  }
  
  /**
   * Get cases resolved today
   */
  public getCasesResolvedToday(): CollectionCase[] {
    // In a real system, this would check resolution date
    // For mock data, we'll return a subset of 'Closed' cases
    return this.cases.filter(c => c.status === 'Closed').slice(0, 3);
  }
  
  // ============================================
  // RECEIPTS QUERIES
  // ============================================
  
  /**
   * Get all receipts
   */
  public getAllReceipts(): Receipt[] {
    return this.receipts;
  }
  
  /**
   * Get unallocated receipts
   */
  public getUnallocatedReceipts(): Receipt[] {
    return this.receipts.filter(r => r.allocationStatus === 'unallocated');
  }
  
  /**
   * Get receipts received today
   */
  public getReceiptsReceivedToday(): Receipt[] {
    return this.receipts.filter(r => {
      const receiptDate = new Date(r.receiptDate);
      return isToday(receiptDate);
    });
  }
  
  /**
   * Get partially allocated receipts
   */
  public getPartiallyAllocatedReceipts(): Receipt[] {
    return this.receipts.filter(r => r.allocationStatus === 'partially_allocated');
  }
  
  // ============================================
  // DISPUTES QUERIES
  // ============================================
  
  /**
   * Get all disputes
   */
  public getAllDisputes(): any[] {
    return this.disputes;
  }
  
  /**
   * Get disputes requiring response today
   */
  public getDisputesRequiringResponseToday(): any[] {
    const today = new Date();
    return this.disputes.filter(d => {
      const dueDate = new Date(d.dueDate);
      return isToday(dueDate) && d.requiresResponse && d.status !== 'Resolved' && d.status !== 'Closed';
    });
  }
  
  /**
   * Get active disputes
   */
  public getActiveDisputes(): any[] {
    return this.disputes.filter(d => 
      d.status === 'In Progress' || 
      d.status === 'Under Review' || 
      d.status === 'New' ||
      d.status === 'Escalated'
    );
  }
}

// Export singleton instance getter
export const getGlobalDataStore = () => GlobalDataStore.getInstance();

// Export reset function for testing
export const resetGlobalDataStore = () => GlobalDataStore.reset();
